import React, { useCallback, useMemo, useState } from "react";
import { AmpersandProvider } from "@amp-labs/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SearchInput from "@/components/ui/search-input";
import Text from "@/components/ui/text";
import useAccount from "@/entities/accounts/hooks";
import { useSettingsTokens } from "@/entities/settings/hooks";
import { useUsersMe } from "@/entities/users/hooks";
import {
  useContractSubmissionSettings,
  useStripeConnectionSettings
} from "@/entities/stripeCheckout/hooks";
import { useWebFormSettings } from "@/entities/webForm/hooks";
import { useCanAccessAdminSettings } from "@/pages/settings/hooks";
import { AuthRedirectProps } from "@/services/auth/AuthRedirect";
import { ampersandOptions } from "@/pages/Integrations/ampersandOptions";
import AmpersandConnectionStatus, {
  IntegrationStatus
} from "@/pages/settings/components/integrations/AmpersandConnectionStatus";
import IntegrationCard from "@/pages/settings/components/integrations/IntegrationCard";
import IntegrationsNoResults from "@/pages/settings/components/integrations/IntegrationsNoResults";
import IntegrationsTopBar from "@/pages/settings/components/integrations/IntegrationsTopBar";
import IntegrationsSection from "@/pages/settings/components/integrations/IntegrationsSection";
import {
  ApiKeyIntegration,
  INTEGRATIONS_TRANSLATION_PREFIX,
  Integration,
  hasServiceToken,
  useIntegrations
} from "@/pages/settings/components/integrations/integrations";
import { ApiKeyConnectModal } from "@/pages/settings/components/integrations/ApiKeyConnectModal";
import { getServiceConnection } from "@/pages/settings/components/connectedAccounts/serviceConnection";

interface IntegrationsSettingsProps {
  onAuthRedirect: (props: AuthRedirectProps) => void;
}

export const IntegrationsSettings: React.FC<IntegrationsSettingsProps> = ({
  onAuthRedirect
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data: tokensData, isLoading: isLoadingTokens } = useSettingsTokens();
  const accountId = useAccount().data?.id;
  const userId = useUsersMe().data?.id;
  const visibleIntegrations = useIntegrations();
  // The website form's settings are admin-only, so anyone else never asks.
  const isSettingsAdmin = useCanAccessAdminSettings();
  const showWebForm = visibleIntegrations.some(({ id }) => id === "webForm");
  const webFormQuery = useWebFormSettings(isSettingsAdmin && showWebForm);
  const showStripeCheckout = visibleIntegrations.some(
    ({ id }) => id === "stripeCheckout"
  );
  const stripeConnectionQuery = useStripeConnectionSettings(
    isSettingsAdmin && showStripeCheckout
  );
  const submissionQuery = useContractSubmissionSettings(
    isSettingsAdmin && showStripeCheckout
  );
  const [keyIntegration, setKeyIntegration] =
    useState<ApiKeyIntegration | null>(null);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [ampersandStatuses, setAmpersandStatuses] = useState<
    Record<string, IntegrationStatus>
  >({});

  const handleAmpersandStatus = useCallback(
    (integrationId: string, status: IntegrationStatus) =>
      setAmpersandStatuses((previous) => ({
        ...previous,
        [integrationId]: status
      })),
    []
  );

  const integrations = useMemo(
    () =>
      visibleIntegrations
        .map((integration) => {
          const status: IntegrationStatus = hasServiceToken(integration)
            ? {
                isConnected: getServiceConnection(
                  tokensData?.tokens,
                  integration.serviceId
                ).allTokensValid,
                isLoading: isLoadingTokens,
                isError: false
              }
            : integration.id === "stripeCheckout"
              ? {
                  // Connected once purchases would arrive and become contracts.
                  isConnected:
                    isSettingsAdmin &&
                    (stripeConnectionQuery.data?.configured ?? false) &&
                    (submissionQuery.data?.sources.stripe.enabled ?? false),
                  isLoading:
                    isSettingsAdmin &&
                    (stripeConnectionQuery.isLoading ||
                      submissionQuery.isLoading),
                  isError:
                    isSettingsAdmin &&
                    (stripeConnectionQuery.isError || submissionQuery.isError)
                }
              : integration.kind === "native"
                ? {
                    isConnected: webFormQuery.data?.enabled ?? false,
                    isLoading: isSettingsAdmin && webFormQuery.isLoading,
                    isError: isSettingsAdmin && webFormQuery.isError
                  }
                : (ampersandStatuses[integration.id] ?? {
                    isConnected: false,
                    isLoading: true,
                    isError: false
                  });
          return {
            integration,
            name: t(
              `${INTEGRATIONS_TRANSLATION_PREFIX}.items.${integration.id}.name`
            ),
            ...status
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [
      t,
      visibleIntegrations,
      tokensData,
      isLoadingTokens,
      ampersandStatuses,
      webFormQuery.data,
      webFormQuery.isLoading,
      webFormQuery.isError,
      stripeConnectionQuery.data,
      stripeConnectionQuery.isLoading,
      stripeConnectionQuery.isError,
      submissionQuery.data,
      submissionQuery.isLoading,
      submissionQuery.isError,
      isSettingsAdmin
    ]
  );

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = integrations.filter(({ name }) =>
    name.toLowerCase().includes(normalizedSearch)
  );
  const connected = filtered.filter(({ isConnected }) => isConnected);
  const available = filtered.filter(({ isConnected }) => !isConnected);
  const isGrouped = integrations.some(({ isConnected }) => isConnected);

  const openIntegration = (integration: Integration) =>
    navigate(`/settings/integrations/${integration.id}`);

  const handleConnect = (integration: Integration) => {
    if (integration.kind === "ampersand" || integration.kind === "native") {
      openIntegration(integration);
      return;
    }
    if (integration.kind === "apiKey") {
      setKeyIntegration(integration);
      setIsKeyModalOpen(true);
      return;
    }
    onAuthRedirect({
      to: integration.registerTo,
      method: "POST",
      redirectTo: "/settings/integrations"
    });
  };

  const renderCards = (items: typeof integrations) =>
    items.map(({ integration, isConnected, isLoading, isError }) => (
      <IntegrationCard
        key={integration.id}
        integration={integration}
        isConnected={isConnected}
        isLoading={isLoading}
        isError={isError}
        onOpen={() => openIntegration(integration)}
        onConnect={() => handleConnect(integration)}
      />
    ));

  const renderContent = () => {
    if (isLoadingTokens) return null;

    if (filtered.length === 0) {
      return <IntegrationsNoResults onClearSearch={() => setSearch("")} />;
    }

    if (!isGrouped) {
      return (
        <IntegrationsSection data-test="integrations-all">
          {renderCards(filtered)}
        </IntegrationsSection>
      );
    }

    return (
      <div className="flex w-full flex-col gap-12">
        {connected.length > 0 && (
          <IntegrationsSection
            title={t(`${INTEGRATIONS_TRANSLATION_PREFIX}.connected`)}
            count={connected.length}
            className="gap-2"
            data-test="integrations-connected"
          >
            {renderCards(connected)}
          </IntegrationsSection>
        )}
        {available.length > 0 && (
          <IntegrationsSection
            title={t(`${INTEGRATIONS_TRANSLATION_PREFIX}.available`)}
            count={available.length}
            className="gap-4"
            data-test="integrations-available"
          >
            {renderCards(available)}
          </IntegrationsSection>
        )}
      </div>
    );
  };

  return (
    <div
      className="flex w-full flex-col"
      data-test="settings-integrations-page"
    >
      {accountId && userId && (
        <AmpersandProvider options={ampersandOptions}>
          {visibleIntegrations
            .filter((integration) => integration.kind === "ampersand")
            .map((integration) => (
              <AmpersandConnectionStatus
                key={integration.id}
                integrationId={integration.id}
                groupRef={accountId}
                consumerRef={userId}
                onStatus={handleAmpersandStatus}
              />
            ))}
        </AmpersandProvider>
      )}
      <IntegrationsTopBar />
      <div className="mb-4 flex w-full items-center justify-center">
        <div className="flex w-[800px] flex-col items-start justify-start gap-12 pt-12">
          <div className="flex w-full flex-col gap-6">
            <div className="flex flex-col">
              <Text variant="title3H1">
                {t(`${INTEGRATIONS_TRANSLATION_PREFIX}.title`)}
              </Text>
              <Text variant="bodySecondary">
                {t(`${INTEGRATIONS_TRANSLATION_PREFIX}.description`)}
              </Text>
            </div>
            <SearchInput
              placeholder={t(
                `${INTEGRATIONS_TRANSLATION_PREFIX}.searchPlaceholder`
              )}
              value={search}
              onChange={setSearch}
            />
          </div>
          {renderContent()}
        </div>
      </div>
      {keyIntegration && (
        <ApiKeyConnectModal
          integration={keyIntegration}
          name={t(
            `${INTEGRATIONS_TRANSLATION_PREFIX}.items.${keyIntegration.id}.name`
          )}
          isOpen={isKeyModalOpen}
          onClose={() => setIsKeyModalOpen(false)}
        />
      )}
    </div>
  );
};
